/* A level 1 character as the builder records it, and the numbers its sheet shows. */

import { BRAWLERS_STRIKE, Catalog, Weapon } from './catalog';
import { AUGMENTS, IKONIS_RANGES, MOTHERBOARD } from './content/motherboard';
import { classes, DhClass } from './content/classes';
import { ActiveBeastform, EVOLUTION_HOPE, extraStress, formStats } from './beastform';
import allDomainCards from './content/domains/cards';
import { ownedCards, tierOf } from './leveling';

export const TRAITS = ['agility', 'strength', 'finesse', 'instinct', 'presence', 'knowledge'] as const;
export type Trait = (typeof TRAITS)[number];

/** The values distributed across the six traits at character creation. */
export const TRAIT_POOL = [-1, 0, 0, 1, 1, 2];

export const TRAIT_VERBS: Record<Trait, string[]> = {
  agility: ['Sprint', 'Leap', 'Maneuver'],
  strength: ['Lift', 'Smash', 'Grapple'],
  finesse: ['Control', 'Hide', 'Tinker'],
  instinct: ['Perceive', 'Sense', 'Navigate'],
  presence: ['Charm', 'Perform', 'Deceive'],
  knowledge: ['Recall', 'Analyze', 'Comprehend'],
};

export const DESCRIPTION_LINES = [
  { key: 'clothes', label: 'Clothes that are' },
  { key: 'eyes', label: 'Eyes like' },
  { key: 'body', label: "Body that's" },
  { key: 'skin', label: 'Skin the color of' },
  { key: 'attitude', label: 'Attitude like' },
] as const;
export type DescriptionKey = (typeof DESCRIPTION_LINES)[number]['key'];

export type AdvancementKind =
  | 'traits' | 'hp' | 'stress' | 'experiences' | 'domainCard' | 'evasion' | 'subclass' | 'proficiency' | 'multiclass';

/** One marked level-up option, with the picks it needs. */
export interface Advancement {
  kind: AdvancementKind;
  /** The tier whose list it was marked on. */
  tier: number;
  traits?: Trait[];
  /** Indexes into the Experience list. */
  experiences?: number[];
  card?: string;
}

/** Everything chosen when reaching a level. */
export interface LevelUp {
  level: number;
  /** The tier achievement's new Experience (levels 2, 5 and 8). */
  experience?: string;
  advancements: Advancement[];
  /** Step four's new domain card. */
  card: string;
  /** An optional exchange of an owned domain card. */
  swap?: { from: string; to: string };
  /** The Beastbound companion's training option for this level. */
  companion?: CompanionTraining;
  /** The companion's new Experience, gained with the character's at levels 2, 5 and 8. */
  companionExperience?: string;
  /** Stance Fighter: the martial stance learned this level. */
  stance?: string;
}

/** One marked option on the Ranger Companion sheet's Training list. */
export interface CompanionTraining {
  /** An option name from COMPANION_SHEET.options. */
  option: string;
  /** Intelligent: index into the companion's Experience list. */
  experience?: number;
  /** Vicious: which one steps up. */
  step?: 'damage' | 'range';
}

export interface Multiclass {
  className: string;
  domain: string;
  subclass: string;
}

/** Levels that bring a tier achievement: a new Experience and +1 Proficiency. */
export const TIER_ACHIEVEMENT_LEVELS = [2, 5, 8];

/** Boxes the player marks while playing. */
export type Track = 'hp' | 'stress' | 'hope' | 'armor' | 'handfuls' | 'bags' | 'chest' | 'favor' | 'companionStress' | 'focus';

export interface Character {
  className: string;
  subclass: string;
  ancestry: string;
  /** Mixed ancestry: the second feature comes from this ancestry. Empty when not mixed. */
  mixedAncestry: string;
  community: string;
  /** The one transformation the character has, by name, or ''. */
  transformation: string;
  traits: Partial<Record<Trait, number>>;
  primary: string;
  secondary: string;
  armor: string;
  /** The armor is taken off: kept, with its marked Armor Slots, but not worn (and, by the rules, not in the inventory). */
  armorOff: boolean;
  /** The trait the Brawler's Strike uses. */
  strikeTrait: Trait | '';
  potion: string;
  classItem: string;
  craft: string;
  /** Starting kit items the player took out of the inventory ("a torch"); the rest stay on the sheet. */
  droppedKit: string[];
  /** Names from the Items and Consumables lists, any rarity. */
  loot: string[];
  /** Up to two spare weapons from any tier, for the sheet's Inventory Weapon slots. */
  inventoryWeapons: string[];
  /** Uses an Ikonis (the Motherboard campaign frame, or a homebrew game) instead of primary and secondary weapons. */
  useIkonis: boolean;
  ikonis: Ikonis;
  description: Partial<Record<DescriptionKey, string>>;
  background: string[];
  /** The player's own wording of the guide's background questions, by index; '' keeps the guide's. */
  backgroundQuestions: string[];
  name: string;
  pronouns: string;
  experiences: string[];
  /** Clank Purposeful Design: index of the Experience with the +1 bonus, or -1. */
  purposeful: number;
  domainCards: string[];
  connections: string[];
  /** The player's own wording of the guide's connection questions, by index; '' keeps the guide's. */
  connectionQuestions: string[];
  patronName: string;
  /** Elemental Origin's element (Elementalist), or ''. */
  element: string;
  sphere: string;
  strangePattern: string;
  stances: string[];
  /** The martial stance the character is in, or ''. */
  activeStance: string;
  /** The Martial Stances page: a half page to cut out as a side sheet, or a full page. */
  stanceSheet: 'half' | 'full';
  companionName: string;
  companionAnimal: string;
  companionExperiences: string[];
  marks: Partial<Record<Track, number>>;
  levelUps: LevelUp[];
  multiclass: Multiclass | null;
  /** The Druid's current Beastform, while transformed. */
  beastform?: ActiveBeastform;
  finished: boolean;
  /** Where an unfinished character was left: the quick page, or a step of the detailed builder. */
  progress?: { mode: 'quick' | 'build'; step: number };
}

export function emptyCharacter(className = ''): Character {
  return {
    className, subclass: '', ancestry: '', mixedAncestry: '', community: '', transformation: '', traits: {},
    primary: '', secondary: '', armor: '', armorOff: false, strikeTrait: '', potion: '', classItem: '', craft: '', droppedKit: [], loot: [], inventoryWeapons: [], useIkonis: false, ikonis: EMPTY_IKONIS,
    description: {}, background: [], backgroundQuestions: [], name: '', pronouns: '', experiences: ['', ''], purposeful: -1,
    domainCards: [], connections: [], connectionQuestions: [], patronName: '', sphere: '', element: '', strangePattern: '', stances: [], activeStance: '', stanceSheet: 'half',
    companionName: '', companionAnimal: '', companionExperiences: ['', ''], marks: {}, levelUps: [], multiclass: null, finished: false,
  };
}

/** A new character with nothing chosen yet; moving between builder steps (`progress`) is not a choice. */
export function isUntouched(c: Character): boolean {
  const blank = emptyCharacter();
  return (Object.keys(blank) as Array<keyof Character>).every((k) => JSON.stringify(c[k]) === JSON.stringify(blank[k]));
}

/** An Ikonis and the Motherboard module's records: its design, augments, scrap and Quantum. */
export interface Ikonis {
  name: string;
  description: string;
  trait: Trait | '';
  /** One of IKONIS_RANGES, which also sets the damage dice. */
  range: string;
  damageType: string;
  /** How many of each augment are built. */
  built: Record<string, number>;
  /** Built augments installed, by name; an augment built twice can be installed twice. */
  installed: string[];
  /** Scrap counts by name (Gear, Crystal...). */
  scrap: Record<string, number>;
  relics: string[];
  quantum: number;
}

export const EMPTY_IKONIS: Ikonis = { name: '', description: '', trait: '', range: '', damageType: '', built: {}, installed: [], scrap: {}, relics: [], quantum: 0 };

/** The Ikonis as the primary weapon the sheet and the stats read: two-handed, Bonded, and its installed augments as its features. */
export function ikonisWeapon(c: Character): Weapon | undefined {
  if (!c.useIkonis) return undefined;
  const { name, trait, range, damageType, installed } = c.ikonis;
  const augments = installed.flatMap((n) => AUGMENTS.filter((a) => a.name === n));
  return {
    name: name || 'Ikonis',
    trait: trait && trait[0].toUpperCase() + trait.slice(1),
    range,
    damage: IKONIS_RANGES.find((r) => r.range === range)?.damage ?? '',
    damageType,
    burden: 'Two-Handed',
    feature: [MOTHERBOARD.bonded, ...augments].map((f) => `${f.name}: ${f.text}`).join(' '),
  };
}

/** The weapons in the character's hands: the Ikonis, or the chosen primary and secondary weapons. */
/** The armor the character wears: none while it is taken off. */
export const wornArmor = (c: Character, catalog: Catalog) => (c.armorOff ? undefined : catalog.armor(c.armor));

export const activeWeapons = (c: Character, catalog: Catalog) =>
  c.useIkonis ? { primary: ikonisWeapon(c), secondary: undefined } : { primary: catalog.weapon(c.primary), secondary: catalog.weapon(c.secondary) };

/** A new class keeps heritage, traits, gear and story but clears every class-specific choice. */
/** The questions as asked: the player's own wording where there is one, else the guide's. */
export const askedQuestions = (guide: string[] = [], own: string[] = []) => guide.map((q, i) => own[i]?.trim() || q);

export function withClass(c: Character, className: string): Character {
  if (c.className === className) return c;
  const { ancestry, mixedAncestry, community, transformation, traits, secondary, armor, armorOff, potion, loot, inventoryWeapons, useIkonis, ikonis, name, pronouns, experiences, purposeful, marks, progress } = c;
  const primary = c.primary === BRAWLERS_STRIKE.name ? '' : c.primary;
  return {
    ...emptyCharacter(className),
    ancestry, mixedAncestry, community, transformation, traits, primary, secondary, armor, armorOff, potion, loot, inventoryWeapons, useIkonis, ikonis, name, pronouns, experiences, purposeful, marks, progress,
  };
}

const firstOf = (v?: string | string[]) => (Array.isArray(v) ? v[0] : v) ?? '';

/** The class's suggested weapons and armor; the secondary weapon only beside a one-handed primary. */
export function suggestedGear(c: Character, catalog: Catalog): Pick<Character, 'primary' | 'secondary' | 'armor' | 'armorOff'> {
  const stats = catalog.cls(c.className)?.stats ?? {};
  const primary = catalog.weapon(firstOf(stats.suggestedPrimary));
  return {
    primary: primary?.name ?? c.primary,
    secondary: primary?.burden === 'One-Handed' ? catalog.weapon(firstOf(stats.suggestedSecondary))?.name ?? '' : '',
    armor: catalog.armor(stats.suggestedArmor ?? '')?.name ?? c.armor,
    armorOff: false,
  };
}

/** Starting marks: 2 Hope, a handful of gold and (Warlock) 3 Favor. */
const START_MARKS: Partial<Record<Track, number>> = { hope: 2, handfuls: 1, favor: 3 };
export const markCount = (c: Character, track: Track) => c.marks[track] ?? START_MARKS[track] ?? 0;

/** Marks a track; marking the last Hit Point drops a Druid out of their Beastform. */
export function markTrack(c: Character, catalog: Catalog, track: Track, count: number): Character {
  const { beastform, ...next } = { ...c, marks: { ...c.marks, [track]: count } };
  return beastform && track === 'hp' && count >= deriveStats(c, catalog).hp ? next : { ...next, beastform };
}

/** Sheet parts a saved PDF can leave empty, to fill in by hand after printing. */
export const BLANK_PARTS = ['Hit Points', 'Stress', 'Hope', 'Armor Slots', 'Gold', 'Trait marks', 'Level', 'Traits', 'Evasion', 'Experience', 'Active weapons', 'Active armor', 'Inventory'] as const;
export type BlankPart = typeof BLANK_PARTS[number];

const BLANK_MARKS: Partial<Record<BlankPart, Track[]>> = {
  'Hit Points': ['hp'], Stress: ['stress', 'companionStress'], Hope: ['hope'], 'Armor Slots': ['armor'], Gold: ['handfuls', 'bags', 'chest'],
};

/** The character as a printed copy shows it, with the chosen parts emptied. The sheet empties the class's Inventory lines, Trait marks, Level, Traits, Evasion and Experiences itself. */
export function blankOut(c: Character, parts: readonly BlankPart[]): Character {
  const marks = { ...c.marks };
  parts.forEach((p) => BLANK_MARKS[p]?.forEach((t) => { marks[t] = 0; }));
  return {
    ...c,
    marks,
    ...(parts.includes('Active weapons') && { primary: '', secondary: '', strikeTrait: '' as const }),
    ...(parts.includes('Active armor') && { armor: '' }),
    // The starting item choices go too, so the guide leaves them unmarked.
    ...(parts.includes('Inventory') && { loot: [], inventoryWeapons: [], potion: '', classItem: '', craft: '' }),
  };
}

/** Takes on a Beastform, marking a Stress (and a hybrid's extra Stress) or, with Evolution, spending Hope instead. */
export function transform(c: Character, catalog: Catalog, active: ActiveBeastform, pay: 'stress' | 'hope'): Character {
  const stress = extraStress(catalog.beastforms.find((f) => f.name === active.name)) + (pay === 'stress' ? 1 : 0);
  const marks = { ...c.marks };
  if (stress) marks.stress = markCount(c, 'stress') + stress;
  if (pay === 'hope') marks.hope = markCount(c, 'hope') - EVOLUTION_HOPE;
  return { ...c, marks, beastform: active };
}

/** Reads a class suggestion such as "0, -1, +1, 0, +2, +1" in sheet order. */
export function parseTraits(raw: string): Record<Trait, number> {
  const values = raw.split(',').map((v) => Number(v.trim().replace('−', '-')));
  return Object.fromEntries(TRAITS.map((t, i) => [t, values[i]])) as Record<Trait, number>;
}

export function freeTraitValues(traits: Partial<Record<Trait, number>>): number[] {
  const free = [...TRAIT_POOL];
  Object.values(traits).forEach((v) => {
    const i = free.indexOf(v as number);
    if (i >= 0) free.splice(i, 1);
  });
  return free;
}

type Stat = 'evasion' | 'armorScore' | 'thresholds' | 'hp' | 'stress' | Trait;
export type Mods = Partial<Record<Stat, number>>;

// "to" is optional: Ikonis augments read "+1 Armor Score", equipment "+1 to Evasion".
const MOD_PATTERN = /([+-]\d+)(?: bonus)?(?: to (?:your )?| )(Evasion|Armor Score|damage thresholds|Agility|Strength|Finesse|Instinct|Presence|Knowledge)/gi;
const STAT_NAMES: Record<string, Stat> = { evasion: 'evasion', 'armor score': 'armorScore', 'damage thresholds': 'thresholds' };

/** Stat changes written in an equipment feature, such as "Heavy: -1 to Evasion". */
export function featureMods(text?: string | null): Mods {
  const mods: Mods = {};
  Array.from((text ?? '').replace(/−/g, '-').matchAll(MOD_PATTERN)).forEach(([, n, name]) => {
    const stat = STAT_NAMES[name.toLowerCase()] ?? (name.toLowerCase() as Trait);
    mods[stat] = (mods[stat] ?? 0) + Number(n);
  });
  return mods;
}

/** Ancestry features that change a starting number. (Shell adds Proficiency; see deriveStats.) */
const HERITAGE_MODS: Record<string, Mods> = {
  Endurance: { hp: 1 },
  'High Stamina': { stress: 1 },
  Nimble: { evasion: 1 },
  Stoneskin: { armorScore: 1, thresholds: 1 },
};

export function ancestryFeatures(c: Character, catalog: Catalog) {
  const features = catalog.ancestry(c.ancestry)?.features ?? [];
  const mixed = catalog.ancestry(c.mixedAncestry)?.features;
  return mixed ? [features[0], mixed[1]].filter(Boolean) : features;
}

export function spellcastTrait(c: Character, catalog: Catalog): Trait | undefined {
  return catalog.cls(c.className)?.subclasses?.[c.subclass]?.spellcastTrait?.toLowerCase() as Trait | undefined;
}

// Domain cards that change the armor numbers, read from their own text:
// "While you're wearing armor, gain a +1 bonus to your Armor Score." (Armorer; Fortified Armor's thresholds alike)
const WHILE_WORN = /^While you(?:'re| are) wearing armor,/;
// "When you choose not to equip armor, you have a base Armor Score of 3 + your Strength ... Tier 1: 9/19" (Bare Bones)
const NO_ARMOR = /^When you choose not to equip armor, you have a base Armor Score of (\d+) \+ your (\w+)/;

/** The cards that let a character go without armor (Bare Bones), so armor is not a required choice for them. */
const NO_ARMOR_CARDS = new Set(allDomainCards.filter((k) => NO_ARMOR.test(k.text)).map((k) => k.name));

/** The character's card that lets it go without armor (Bare Bones), if it has one. */
export const noArmorCard = (c: Character) => ownedCards(c).find((name) => NO_ARMOR_CARDS.has(name));

/** A no-armor card's base Armor Score (its number plus a trait) and its base thresholds for the tier. */
function bareArmor(card: { name: string; text: string }, tier: number) {
  const base = NO_ARMOR.exec(card.text);
  const thresholds = new RegExp(`Tier ${tier}: (\\d+)/(\\d+)`).exec(card.text);
  if (!base || !thresholds) return undefined;
  return { name: card.name, score: Number(base[1]), trait: base[2].toLowerCase() as Trait, major: Number(thresholds[1]), severe: Number(thresholds[2]) };
}

export function deriveStats(c: Character, catalog: Catalog) {
  const cls = catalog.cls(c.className);
  const armor = wornArmor(c, catalog);
  // Armor taken off leaves the character unarmored (the Armor rules): base Armor Score 0, thresholds of level and twice level.
  const unarmored = c.armorOff && !!c.armor;
  const cards = ownedCards(c).flatMap((name) => catalog.card(name) ?? []);
  const heritage = ancestryFeatures(c, catalog);
  const advancements = c.levelUps.flatMap((l) => l.advancements);
  const beastform = c.beastform ? formStats(c.beastform, catalog) : null;
  const raised = (stat: Stat): Mods => ({ [stat]: advancements.filter((a) => a.kind === stat).length });
  const mods = [
    ...[armor?.feature, ...Object.values(activeWeapons(c, catalog)).map((w) => w?.feature)].map(featureMods),
    ...(armor ? cards.filter((k) => WHILE_WORN.test(k.text)).map((k) => featureMods(k.text.split('\n')[0])) : []),
    ...heritage.map((f) => HERITAGE_MODS[f.name] ?? {}),
    raised('hp'), raised('stress'), raised('evasion'),
    ...advancements.flatMap((a) => a.traits ?? []).map((t): Mods => ({ [t]: 1 })),
    beastform ? { [beastform.trait]: beastform.traitBonus, evasion: beastform.evasionBonus } : {},
    beastform && c.beastform?.evolution ? { [c.beastform.evolution]: 1 } : {},
  ];
  const sum = (stat: Stat) => mods.reduce((total, m) => total + (m[stat] ?? 0), 0);
  const level = 1 + c.levelUps.length;
  const proficiency = 1 + TIER_ACHIEVEMENT_LEVELS.filter((l) => l <= level).length + advancements.filter((a) => a.kind === 'proficiency').length;
  const spellTrait = spellcastTrait(c, catalog);
  const enchanted = armor?.feature?.startsWith('Enchanted') && spellTrait ? c.traits[spellTrait] ?? 0 : 0;
  const shell = heritage.some((f) => f.name === 'Shell') ? proficiency : 0;
  const thresholdBonus = level + sum('thresholds') + enchanted + shell;
  const traits = Object.fromEntries(
    TRAITS.map((t) => [t, c.traits[t] === undefined ? null : (c.traits[t] as number) + sum(t)]),
  ) as Record<Trait, number | null>;
  const bare = armor ? undefined : cards.map((k) => bareArmor(k, tierOf(level))).find(Boolean);
  /** The Active Armor box: the worn armor, or a no-armor card with its base thresholds and base score. */
  const armorBox = armor ?? (bare && { name: bare.name, major: bare.major, severe: bare.severe, score: bare.score + (traits[bare.trait] ?? 0), feature: null });

  return {
    level,
    proficiency,
    hope: 2,
    evasion: cls ? Number(cls.stats?.evasion ?? 0) + sum('evasion') : null,
    armorScore: armorBox ? armorBox.score + sum('armorScore') : unarmored ? sum('armorScore') : null,
    major: armorBox ? armorBox.major + thresholdBonus : unarmored ? level : null,
    severe: armorBox ? armorBox.severe + thresholdBonus : unarmored ? 2 * level : null,
    armorBox,
    hp: cls ? Number(cls.stats?.hp ?? 0) + sum('hp') : 0,
    stress: 6 + sum('stress'),
    traits,
    beastform,
  };
}

export type Stats = ReturnType<typeof deriveStats>;

/** The required choices of each step, named as the builder asks for them. Steps 4 and 9 have none. */
/** The class's "take" kit as items ("a torch", "50 feet of rope", "basic supplies"); its handful of gold is the Gold track's first mark, not an item. */
export const kitItems = (take = '') => take.split(/,\s*(?:and\s+)?/).map((item) => item.trim()).filter((item) => item && !/handful of gold/i.test(item));

/** An item as the sheet and builder list it, without the guide's leading article ("a torch" is "torch"); the data keeps the guide's wording. */
export const itemName = (item: string) => item.replace(/^an?\s+/i, '');

/** The starting kit items the character still carries, after any the player removed. */
export const keptKit = (c: Character, cls?: DhClass) => kitItems(cls?.inventory?.take).filter((item) => !c.droppedKit.includes(item));

/** True when the character's subclass has a Martial Stances sheet (the Brawler's Martial Artist), in any version. */
export const fightsInStances = (c: Character) => classes.some((k) => k.name === c.className && !!k.subclasses?.[c.subclass]?.martialForm);

const STEP_NEEDS: Record<number, Array<[string, (c: Character) => boolean]>> = {
  1: [['class', (c) => !!c.className], ['subclass', (c) => !!c.subclass], ['two martial stances', (c) => !fightsInStances(c) || c.stances.length >= 2]],
  2: [['ancestry', (c) => !!c.ancestry], ['community', (c) => !!c.community]],
  3: [['traits', (c) => TRAITS.every((t) => c.traits[t] !== undefined)]],
  5: [
    // A weapon put away in the inventory still counts as chosen.
    ['primary weapon', (c) => c.useIkonis || !!c.primary || c.inventoryWeapons.length > 0],
    ["Brawler's Strike trait", (c) => c.useIkonis || c.primary !== BRAWLERS_STRIKE.name || !!c.strikeTrait],
    ['Ikonis', (c) => !c.useIkonis || (!!c.ikonis.trait && !!c.ikonis.range && !!c.ikonis.damageType)],
    // Bare Bones lets a character choose not to equip armor.
    ['armor', (c) => !!c.armor || c.domainCards.some((name) => NO_ARMOR_CARDS.has(name))],
    ['potion', (c) => !!c.potion],
    ['class item', (c) => !!c.classItem],
  ],
  6: [['name', (c) => !!c.name.trim()]],
  7: [['two Experiences', (c) => c.experiences.filter((e) => e.trim()).length >= 2]],
  8: [['two domain cards', (c) => c.domainCards.length === 2]],
};

/** What a step still needs, by name ("potion", "class item"). */
export const missingParts = (step: number, c: Character) =>
  (STEP_NEEDS[step] ?? []).filter(([, done]) => !done(c)).map(([name]) => name);

/** Whether a named required choice ("potion") is still missing, for lighting the panel that holds it. */
export const needs = (c: Character, part: string) =>
  Object.values(STEP_NEEDS).flat().some(([name, done]) => name === part && !done(c));

export const stepDone = (step: number, c: Character) => (STEP_NEEDS[step] ? !missingParts(step, c).length : !!c.className);

export const missingSteps = (c: Character) =>
  Object.keys(STEP_NEEDS).map(Number).filter((step) => missingParts(step, c).length);
